import { useLazyQuery } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    makeStyles,
    RadioGroup,
    TextField,
    Theme,
    Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { GetPplFamilyAndPtAutoSuggest, PPLFamilyAndPTAutoSuggestInput, PPLFamilyAndPTAutoSuggestResult } from 'api/graphQL/queries/productFamilyAutosuggest.queries'
import debounce from 'lodash.debounce'
import React, { useMemo } from 'react'
import { toTitleCase } from 'shared/utils/stringUtils'
import ManageUnmappedDialogActions from './DialogActions'
import ManageUnmappedDialogTitle from './DialogTitle'
import RadioButton from './RadioButton'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(0, 3, 2),
    },
    subtitle: {
        fontSize: pxToRem(22),
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        color: '#304959',
    },
    autocomplete: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
        },
    },
    formLabel: {
        minWidth: pxToRem(200),
        fontSize: pxToRem(14),
        marginBottom: theme.spacing(1),
        color: '#304959',
        alignItems: 'center',
        marginRight: theme.spacing(1),
        wrap: "nowrap"
    },
    formControlWrapper: {
        display: "flex",
        width: '100%',
        alignItems: "center"
    },
    radioGroup: {
        marginLeft: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    selectDropdown: {
        width: '100%',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
        },
    },
}))

/**
 * Form data for adding a product to an existing family
 */
export interface AddToExistingFamilyFormData {
    /** The selected family (key-value pair with family ID and display name) */
    selectedFamily: Record<string, string> | null
    /** Whether to use an existing product type or create a new one */
    selectedOption: 'existing' | 'new'
    /** The name of an existing product type (when selectedOption is 'existing') */
    existingProductType?: string
    /** The name for a new product type (when selectedOption is 'new') */
    newProductTypeName?: string
}

/**
 * Props for the AddToExistingFamily component
 */
export interface AddToExistingFamilyProps {
    /** Whether the dialog is open */
    open: boolean
    /** Callback when the dialog should be closed */
    onClose: () => void
    /** Callback when form is saved and user continues */
    onSaveAndContinue: (formData: AddToExistingFamilyFormData) => void
}

/**
 * Dialog for adding an unmapped product to an existing family.
 * 
 * Provides a form to:
 * 1. Select an existing MasterSpec family via autocomplete
 * 2. Choose whether to use an existing product type or create a new one
 * 3. Select/enter the product type information
 * 
 * Uses GraphQL queries to fetch family and product type suggestions.
 * Includes debounced search functionality for better performance.
 * 
 * @param props - The component props
 * @returns The AddToExistingFamily component
 */
const AddToExistingFamily: React.FC<AddToExistingFamilyProps> = ({
    open,
    onClose,
    onSaveAndContinue,
}) => {
    const classes = useStyles()
    const [formData, setFormData] = React.useState<AddToExistingFamilyFormData>({
        selectedFamily: null,
        selectedOption: 'existing',
        existingProductType: '',
        newProductTypeName: '',
    })
    const [families, setFamilies] = React.useState<string[]>([])
    const [productTypes, setProductTypes] = React.useState<string[]>([])

    const formattedFamilies = useMemo(() => {
        return families.map(family => {
            const tempFamily = family.split("|")
            const familyNumber = tempFamily[0]
            const familyName = tempFamily[1]
            return { key: familyNumber, value: `${familyNumber} - ${familyName}` }
        })
    }, [families])

    // Lazy queries for autocomplete
    const [searchFamilies, { loading: familiesLoading }] = useLazyQuery<PPLFamilyAndPTAutoSuggestResult>(
        GetPplFamilyAndPtAutoSuggest,
        {
            onCompleted: (data) => {
                setFamilies(data?.getPPLFamilyAndPTAutoSuggest?.result || [])
            },
            onError: (error) => {
                console.error('Error fetching families:', error)
                setFamilies([])
            }
        }
    )

    const [searchProductTypes, { loading: productTypesLoading }] = useLazyQuery<PPLFamilyAndPTAutoSuggestResult>(
        GetPplFamilyAndPtAutoSuggest,
        {
            onCompleted: (data) => {
                setProductTypes(data?.getPPLFamilyAndPTAutoSuggest?.result || [])
            },
            onError: (error) => {
                console.error('Error fetching product types:', error)
                setProductTypes([])
            }
        }
    )

    // Debounced search functions
    const debouncedSearchFamilies = React.useMemo(
        () => debounce((searchText: string) => {
            searchFamilies({
                variables: {
                    input: {
                        searchText,
                        maxResults: 100,
                        searchProductTypes: false,
                    } as PPLFamilyAndPTAutoSuggestInput
                }
            })
        }, 300),
        [searchFamilies]
    )

    const debouncedSearchProductTypes = React.useMemo(
        () => debounce((searchText: string, familyName: string) => {
            if (familyName) {
                searchProductTypes({
                    variables: {
                        input: {
                            searchText,
                            maxResults: 100,
                            searchProductTypes: true,
                            productFamilyName: familyName,
                        } as PPLFamilyAndPTAutoSuggestInput
                    }
                })
            }
        }, 300),
        [searchProductTypes]
    )

    const handleFamilyChange = (event: any, newValue: Record<string, string> | null) => {
        setFormData(prev => ({
            ...prev,
            selectedFamily: newValue || null,
            existingProductType: '',
        }))
    }

    const handleProductTypeOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'existing' | 'new'
        setFormData(prev => ({
            ...prev,
            selectedOption: value,
            existingProductType: '',
            newProductTypeName: '',
        }))
    }

    const handleExistingProductTypeChange = (event: any, newValue: string | null) => {
        setFormData(prev => ({
            ...prev,
            existingProductType: newValue || '',
        }))
    }

    const handleNewProductTypeNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            newProductTypeName: event.target.value,
        }))
    }

    const handleSaveAndContinue = () => {
        if (isFormValid) {
            onSaveAndContinue(formData)
        }
    }

    const handleClose = () => {
        setFormData({
            selectedFamily: null,
            selectedOption: 'existing',
            existingProductType: '',
            newProductTypeName: '',
        })
        setFamilies([])
        setProductTypes([])
        debouncedSearchFamilies.cancel()
        debouncedSearchProductTypes.cancel()
        onClose()
    }

    const isFormValid = React.useMemo(() => {
        // Family selection is always required
        if (!formData.selectedFamily) return false

        if (formData.selectedOption === 'existing') {
            // For existing product type, need both family and existing product type
            return Boolean(formData.selectedFamily && formData.existingProductType)
        } else {
            // For new product type, need family and new product type name
            return Boolean(formData.selectedFamily && formData.newProductTypeName?.trim())
        }
    }, [formData])

    React.useEffect(() => {
        if (open) {
            // Trigger initial family search when dialog opens
            debouncedSearchFamilies('')
        } else {
            // Clean up when dialog closes
            setFormData({
                selectedFamily: null,
                selectedOption: 'existing',
                existingProductType: '',
                newProductTypeName: '',
            })
            setFamilies([])
            setProductTypes([])
            debouncedSearchFamilies.cancel()
            debouncedSearchProductTypes.cancel()
        }
    }, [open, debouncedSearchFamilies, debouncedSearchProductTypes])

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose()
                }
            }}
            className={classes.dialog}
            aria-labelledby="add-to-existing-family-dialog-title"
        >
            <ManageUnmappedDialogTitle
            >
                Manage Unmapped Product
            </ManageUnmappedDialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.subtitle}>
                    Add to Existing Family
                </div>
                <Typography className={classes.formControlWrapper}>
                    <Typography className={classes.formLabel}>Select Existing Family:</Typography>
                    <Autocomplete
                        options={formattedFamilies}
                        value={formData.selectedFamily}
                        onChange={handleFamilyChange}
                        onInputChange={(event, newInputValue) => {
                            debouncedSearchFamilies(newInputValue)
                        }}
                        loading={familiesLoading}
                        className={classes.selectDropdown}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        )}
                        getOptionLabel={
                            option => toTitleCase(option.value)
                        }
                    />
                </Typography>

                <RadioGroup
                    value={formData.selectedOption}
                    onChange={handleProductTypeOptionChange}
                    className={classes.radioGroup}
                >
                    <RadioButton
                        value="existing"
                        label="Add this listing to an existing product type in this family"
                    />
                    <RadioButton
                        value="new"
                        label="Create a new product type for this listing"
                    />
                </RadioGroup>

                {formData.selectedOption === 'existing' && (
                    <Typography className={classes.formControlWrapper}>
                        <Typography className={classes.formLabel}>Select Existing Product Type:</Typography>
                        <Autocomplete
                            options={productTypes}
                            value={formData.existingProductType}
                            onChange={handleExistingProductTypeChange}
                            onInputChange={(event, newInputValue) => {
                                debouncedSearchProductTypes(newInputValue, formData.selectedFamily?.key)
                            }}
                            loading={productTypesLoading}
                            className={classes.selectDropdown}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size='small'
                                    fullWidth
                                />
                            )}
                        />
                    </Typography>
                )}

                {formData.selectedOption === 'new' && (
                    <Typography className={classes.formControlWrapper}>
                        <Typography className={classes.formLabel}>New Product Type Name:</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={formData.newProductTypeName}
                            onChange={handleNewProductTypeNameChange}
                            className={classes.selectDropdown}
                            size="small"
                        />
                    </Typography>
                )}
            </DialogContent>
            <ManageUnmappedDialogActions
                onCancel={handleClose}
                onContinue={handleSaveAndContinue}
                disabled={!isFormValid}
            />

        </Dialog>
    )
}

export default AddToExistingFamily